/**
 * Admin Tools Page
 *
 * Connects to the admin socket namespace, subscribes to rooms snapshot,
 * and displays Redis rooms and connections for monitoring/cleanup validation.
 * Protected by AdminRouter (user must be ADMIN); backend enforces admin on socket.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthProvider';
import { getAuthToken } from '../utils/authToken';
import { useAdminSocket } from '../admin';
import type { AdminSnapshot } from '../admin';

import './AdminToolsPage.css';

const AdminToolsPage: React.FC = () => {
    const { user } = useAuth();
    const token = getAuthToken();
    const {
        isConnected,
        connectError,
        connect,
        disconnect,
        onRoomsSnapshot,
        requestRoomsSnapshot,
    } = useAdminSocket();

    const [snapshot, setSnapshot] = useState<AdminSnapshot | null>(null);
    const [snapshotError, setSnapshotError] = useState<string | null>(null);

    // Connect on mount when we have a user token (AdminRouter ensures admin)
    useEffect(() => {
        if (token && user && !user.guest) {
            connect(token);
        }
        return () => {
            disconnect();
        };
    }, [token, user?.guest]); // eslint-disable-line react-hooks/exhaustive-deps -- connect/disconnect once per token

    // Subscribe to rooms snapshot
    useEffect(() => {
        const unsubscribe = onRoomsSnapshot((payload) => {
            setSnapshot(payload);
            setSnapshotError(null);
        });
        return unsubscribe;
    }, [onRoomsSnapshot]);

    // Request initial snapshot when socket connects
    useEffect(() => {
        if (isConnected) {
            requestRoomsSnapshot();
        }
    }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps -- request once when connected

    return (
        <div className="adminTools">
            <h1>Admin Tools</h1>

            <section className="adminTools__section">
                <div className="adminTools__statusRow">
                    <p>
                        Socket:{' '}
                        <strong>
                            {isConnected
                                ? 'Connected'
                                : connectError
                                  ? 'Error'
                                  : 'Connecting…'}
                        </strong>
                    </p>
                    {connectError && (
                        <p className="adminTools__error" role="alert">
                            {connectError}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={() => requestRoomsSnapshot()}
                        disabled={!isConnected}
                    >
                        Refresh snapshot
                    </button>
                    {snapshot && (
                        <span className="adminTools__lastUpdate">
                            Last update: {new Date(snapshot.at).toLocaleString()}
                        </span>
                    )}
                </div>
            </section>

            {snapshotError && (
                <p className="adminTools__error">{snapshotError}</p>
            )}

            {snapshot && (
                <>
                    <section className="adminTools__section">
                        <h2>Rooms ({snapshot.rooms.length})</h2>
                        {snapshot.rooms.length === 0 ? (
                            <p>No rooms.</p>
                        ) : (
                            <>
                                <h3 className="adminTools__subheading">
                                    With Redis keys ({snapshot.rooms.filter((r) => r.room !== null).length})
                                </h3>
                                {snapshot.rooms.filter((r) => r.room !== null).length === 0 ? (
                                    <p>No rooms with Redis keys.</p>
                                ) : (
                                    <table className="adminTools__table">
                                        <thead>
                                            <tr>
                                                <th className="adminTools__th">Room</th>
                                                <th className="adminTools__th">Phase</th>
                                                <th className="adminTools__th">Status</th>
                                                <th className="adminTools__th">Players</th>
                                                <th className="adminTools__th">TTL (s)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {snapshot.rooms
                                                .filter((r) => r.room !== null)
                                                .map(({ roomCode, room, ttlSeconds }) => (
                                                    <tr key={roomCode}>
                                                        <td className="adminTools__td">{roomCode}</td>
                                                        <td className="adminTools__td">{room?.phase ?? '—'}</td>
                                                        <td className="adminTools__td">{room?.status ?? '—'}</td>
                                                        <td className="adminTools__td">
                                                            {room?.players?.length ?? 0}{' '}
                                                            ({room?.players?.map((p) => p.username).join(', ') ?? '—'})
                                                        </td>
                                                        <td className="adminTools__td">
                                                            {ttlSeconds !== undefined ? ttlSeconds : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                )}
                                <h3 className="adminTools__subheading">
                                    Awaiting cleanup ({snapshot.rooms.filter((r) => r.room === null).length})
                                </h3>
                                {snapshot.rooms.filter((r) => r.room === null).length === 0 ? (
                                    <p>No orphaned room codes.</p>
                                ) : (
                                    <table className="adminTools__table">
                                        <thead>
                                            <tr>
                                                <th className="adminTools__th">Room code</th>
                                                <th className="adminTools__th">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {snapshot.rooms
                                                .filter((r) => r.room === null)
                                                .map(({ roomCode }) => (
                                                    <tr key={roomCode}>
                                                        <td className="adminTools__td">{roomCode}</td>
                                                        <td className="adminTools__td adminTools__td--muted">
                                                            Orphaned — in room_codes, keys expired
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                )}
                            </>
                        )}
                    </section>

                    <section className="adminTools__section">
                        <h2>Connections ({snapshot.connections.length})</h2>
                        {snapshot.connections.length === 0 ? (
                            <p>No connections.</p>
                        ) : (
                            <table className="adminTools__table">
                                <thead>
                                    <tr>
                                        <th className="adminTools__th">Player ID</th>
                                        <th className="adminTools__th">Socket ID</th>
                                        <th className="adminTools__th">Room</th>
                                        <th className="adminTools__th">Status</th>
                                        <th className="adminTools__th">Last seen</th>
                                        <th className="adminTools__th">TTL (s)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {snapshot.connections.map((c) => (
                                        <tr key={c.playerId}>
                                            <td className="adminTools__td">{c.playerId}</td>
                                            <td className="adminTools__td adminTools__td--small">{c.socketId}</td>
                                            <td className="adminTools__td">{c.roomCode || '—'}</td>
                                            <td className="adminTools__td">{c.status}</td>
                                            <td className="adminTools__td">
                                                {c.lastSeen ? new Date(c.lastSeen).toLocaleString() : '—'}
                                            </td>
                                            <td className="adminTools__td">
                                                {c.ttlSeconds !== undefined ? c.ttlSeconds : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default AdminToolsPage;
